import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UtensilsIcon, LeafIcon, ChefHatIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon } from 'lucide-react';
interface CateringPageProps {
  onNavigate: (page: string) => void;
}
export function CateringPage({
  onNavigate
}: CateringPageProps) {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setTimeout(() => {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
    }, 1500);
  };
  const galleryImages = ['https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/WhatsApp-Image-2022-09-16-at-3.51.11-AM.jpeg?fit=900%2C506&ssl=1', 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/catering1.jpeg?fit=900%2C506&ssl=1', 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/WhatsApp-Image-2022-09-16-at-3.51.10-AM-2.jpeg?fit=576%2C1024&ssl=1', 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/catering4.jpeg?fit=900%2C506&ssl=1', 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/WhatsApp-Image-2022-09-16-at-3.51.12-AM-2.jpeg?fit=576%2C1024&ssl=1', 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/09/catering.jpeg?fit=900%2C506&ssl=1'];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-cream pb-0" data-id="element-357">
      {/* 1. Hero Section */}
      <div className="relative bg-charcoal text-white py-32 overflow-hidden" data-id="element-358">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: 'url(https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2021/05/intro-sm-1-e1654854415356.jpg?fit=1161%2C1282&ssl=1)'
      }} data-id="element-359" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/60 to-charcoal" data-id="element-360" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-361">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-7xl uppercase tracking-wide mb-6" data-id="element-362">
            Catering and <span className="text-amber" data-id="element-363">Events</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-body text-xl text-white/90 leading-relaxed max-w-3xl mx-auto" data-id="element-364">
            At Victory Kitchen, we provide professional and well-tailored
            catering services to diverse clienteles. Our catering menu ranges
            from African to Caribbean and American food.
            <br data-id="element-365" />
            <br data-id="element-366" />
            Our catering history includes high schools, top US universities,
            churches, weddings, birthday parties, office parties, anniversaries
            and government agencies.
          </motion.p>
        </div>
      </div>

      {/* 2. Why People Choose Us Section */}
      <div className="py-24 bg-white relative" data-id="element-367">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-368">
          <div className="text-center mb-16" data-id="element-369">
            <h2 className="font-heading text-4xl md:text-5xl text-charcoal uppercase tracking-wide mb-6" data-id="element-370">
              Why People <span className="text-victory-red" data-id="element-371">Choose Us?</span>
            </h2>
            <div className="w-24 h-1 bg-amber mx-auto rounded-full mb-8" data-id="element-372" />
            <p className="font-body text-lg text-charcoal/70 max-w-3xl mx-auto leading-relaxed" data-id="element-373">
              We serve African food with a soul, prepared with high-quality
              organic ingredients mixed with excellent services for your eating
              delight. Our features include BYOB, Catering Services,
              Family-Friendly, Good for Groups, Online Ordering, Private Events,
              Solo Dining and Takeout Services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-374">
            {[{
            icon: <UtensilsIcon className="w-12 h-12 text-amber" data-id="element-375" />,
            title: 'Menu For Every Taste',
            desc: 'We have a variety of menu that includes Nigeria, West African and other Afro-Western sides.'
          }, {
            icon: <LeafIcon className="w-12 h-12 text-green-600" data-id="element-376" />,
            title: 'Fresh Ingredients',
            desc: 'We source and use fresh organic ingredients that remind our visitors of that homestyle African taste.'
          }, {
            icon: <ChefHatIcon className="w-12 h-12 text-victory-red" data-id="element-377" />,
            title: 'Experienced Chefs',
            desc: 'Our Chefs combine knowledge from family, personal culinary experiences and catering trainings to create classic meals.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-cream p-8 rounded-3xl shadow-sm border border-amber/10 text-center hover:shadow-xl hover:-translate-y-2 transition-all duration-300" data-id="element-378">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-charcoal/5" data-id="element-379">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-2xl text-charcoal uppercase mb-4" data-id="element-380">
                  {feature.title}
                </h3>
                <p className="font-body text-charcoal/70 leading-relaxed" data-id="element-381">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* 3. Our Catering Approach Section */}
      <div className="py-24 bg-cream" data-id="element-382">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-383">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-384">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-385">
              <span className="inline-block py-1 px-3 rounded-full bg-amber/20 text-amber font-heading tracking-widest uppercase text-sm mb-4 border border-amber/30" data-id="element-386">
                EVENTS & CATERING SERVICES
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-charcoal uppercase tracking-wide mb-8" data-id="element-387">
                Our Catering <span className="text-victory-red" data-id="element-388">Approach</span>
              </h2>

              <div className="space-y-6 font-body text-lg text-charcoal/80 leading-relaxed mb-10" data-id="element-389">
                <p data-id="element-390">
                  In our catering services, our sole mission is to provide
                  fresh, nutritious, quality and cost effective menu for same
                  day consumption.
                </p>
                <p data-id="element-391">
                  Having gotten varied experiences in catering to many clients,
                  we understand the need to deliver events with efficient cost
                  and great satisfaction.
                </p>
                <p data-id="element-392">
                  Catered food can be picked up from our restaurant, or
                  delivered and served by our catering staff.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-amber/20 shadow-sm mb-10" data-id="element-393">
                <p className="font-heading text-xl text-charcoal uppercase mb-4" data-id="element-394">
                  Reach Our Catering Manager:
                </p>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8" data-id="element-395">
                  <div className="flex items-center gap-3 text-victory-red" data-id="element-396">
                    <PhoneIcon className="w-5 h-5" data-id="element-397" />
                    <span className="font-body font-medium" data-id="element-398">
                      +1 (301) 386-6105
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-victory-red" data-id="element-399">
                    <MailIcon className="w-5 h-5" data-id="element-400" />
                    <span className="font-body font-medium" data-id="element-401">
                      info@victorykitchenz.com
                    </span>
                  </div>
                </div>
              </div>

              <button onClick={() => onNavigate('contact')} className="bg-charcoal hover:bg-amber text-white font-heading text-xl px-10 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-402">
                Book Now
              </button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative" data-id="element-403">
              <div className="absolute -inset-4 bg-gradient-to-br from-amber/20 to-victory-red/20 rounded-3xl transform rotate-3" data-id="element-404" />
              <img src="https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/07/bar-gdd99d7bc7_1920.jpg?fit=950%2C633&ssl=1" alt="Catering Setup" className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]" data-id="element-405" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* 4. Catering Gallery */}
      <div className="py-24 bg-white" data-id="element-406">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-407">
          <div className="text-center mb-16" data-id="element-408">
            <h2 className="font-heading text-4xl text-charcoal uppercase tracking-wide mb-4" data-id="element-409">
              Event <span className="text-amber" data-id="element-410">Gallery</span>
            </h2>
            <div className="w-16 h-1 bg-victory-red mx-auto rounded-full" data-id="element-411" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-id="element-412">
            {galleryImages.map((src, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`rounded-2xl overflow-hidden shadow-md group ${i === 2 || i === 4 ? 'row-span-2' : ''}`} data-id="element-413">
                <img src={src} alt={`Catering Event ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 min-h-[200px]" data-id="element-414" />
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* 5. Working Hours Section */}
      <div className="relative py-32 bg-charcoal text-white overflow-hidden" data-id="element-415">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: 'url(https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2021/04/Meals-001.jpeg?fit=1200%2C800&ssl=1)'
      }} data-id="element-416" />
        <div className="absolute inset-0 bg-charcoal/80" data-id="element-417" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-418">
          <span className="inline-block py-1 px-3 rounded-full bg-white/10 text-white font-heading tracking-widest uppercase text-sm mb-4 border border-white/20" data-id="element-419">
            About Us
          </span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-wide mb-8" data-id="element-420">
            Working <span className="text-amber" data-id="element-421">Hours</span>
          </h2>

          <p className="font-body text-xl text-white/80 leading-relaxed mb-12 max-w-2xl mx-auto" data-id="element-422">
            We are open all through the week into the weekends and offer very
            efficient carryout delivery services. We provide delivery and
            catering services around the DMV metro area.
          </p>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 mb-12 max-w-2xl mx-auto" data-id="element-423">
            <div className="space-y-6" data-id="element-424">
              <div className="flex flex-col sm:flex-row justify-between items-center border-b border-white/10 pb-6" data-id="element-425">
                <div className="flex items-center gap-3 text-amber mb-2 sm:mb-0" data-id="element-426">
                  <CalendarIcon className="w-6 h-6" data-id="element-427" />
                  <span className="font-heading text-2xl uppercase tracking-wide" data-id="element-428">
                    Mon - Thu
                  </span>
                </div>
                <span className="font-body text-xl" data-id="element-429">11:00 am - 9:00 pm</span>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center border-b border-white/10 pb-6" data-id="element-430">
                <div className="flex items-center gap-3 text-amber mb-2 sm:mb-0" data-id="element-431">
                  <CalendarIcon className="w-6 h-6" data-id="element-432" />
                  <span className="font-heading text-2xl uppercase tracking-wide" data-id="element-433">
                    Fri & Sat
                  </span>
                </div>
                <span className="font-body text-xl" data-id="element-434">11:00 am - 9:00 pm</span>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center" data-id="element-435">
                <div className="flex items-center gap-3 text-victory-red mb-2 sm:mb-0" data-id="element-436">
                  <ClockIcon className="w-6 h-6" data-id="element-437" />
                  <span className="font-heading text-2xl uppercase tracking-wide" data-id="element-438">
                    Delivery
                  </span>
                </div>
                <span className="font-body text-xl" data-id="element-439">12:00 noon - 8:00 pm</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-440">
            <button onClick={() => onNavigate('menu')} className="bg-victory-red hover:bg-red-700 text-white font-heading text-xl px-10 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-441">
              Order A Meal
            </button>
            <button onClick={() => onNavigate('contact')} className="bg-transparent border-2 border-white hover:bg-white hover:text-charcoal text-white font-heading text-xl px-10 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-442">
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* 6. Contact Form Section */}
      <div className="py-24 bg-cream relative overflow-hidden" data-id="element-443">
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(#D4A017 2px, transparent 2px)',
        backgroundSize: '30px 30px'
      }} data-id="element-444" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-445">
          <div className="text-center mb-12" data-id="element-446">
            <h2 className="font-heading text-4xl md:text-5xl text-charcoal uppercase tracking-wide mb-4" data-id="element-447">
              Let's Cater For{' '}
              <span className="text-victory-red" data-id="element-448">Your Events</span>
            </h2>
            <p className="font-heading text-lg text-amber uppercase tracking-widest" data-id="element-449">
              Call us on +1 (301) 386-6105 ... OR ... Complete the form below
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-amber/20" data-id="element-450">
            {formStatus === 'success' ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-12" data-id="element-451">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-452">
                  <span className="text-5xl" data-id="element-453">🎉</span>
                </div>
                <h3 className="font-heading text-3xl text-charcoal uppercase mb-4" data-id="element-454">
                  Request Sent!
                </h3>
                <p className="font-body text-charcoal/70 text-lg" data-id="element-455">
                  Thank you for considering Victory Kitchen. Our catering
                  manager will contact you shortly.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-456">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-457">
                  <div data-id="element-458">
                    <label htmlFor="name" className="block font-heading text-sm uppercase text-charcoal/70 mb-2" data-id="element-459">
                      Full Name
                    </label>
                    <input type="text" id="name" required className="w-full bg-cream border border-charcoal/10 rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-all" placeholder="John Doe" data-id="element-460" />
                  </div>
                  <div data-id="element-461">
                    <label htmlFor="email" className="block font-heading text-sm uppercase text-charcoal/70 mb-2" data-id="element-462">
                      Email Address
                    </label>
                    <input type="email" id="email" required className="w-full bg-cream border border-charcoal/10 rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-all" placeholder="john@example.com" data-id="element-463" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-464">
                  <div data-id="element-465">
                    <label htmlFor="phone" className="block font-heading text-sm uppercase text-charcoal/70 mb-2" data-id="element-466">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" required className="w-full bg-cream border border-charcoal/10 rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-all" placeholder="(555) 123-4567" data-id="element-467" />
                  </div>
                  <div data-id="element-468">
                    <label htmlFor="date" className="block font-heading text-sm uppercase text-charcoal/70 mb-2" data-id="element-469">
                      Event Date
                    </label>
                    <input type="date" id="date" required className="w-full bg-cream border border-charcoal/10 rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-all" data-id="element-470" />
                  </div>
                </div>

                <div data-id="element-471">
                  <label htmlFor="message" className="block font-heading text-sm uppercase text-charcoal/70 mb-2" data-id="element-472">
                    Event Details (Guest Count, Location, etc.)
                  </label>
                  <textarea id="message" rows={4} required className="w-full bg-cream border border-charcoal/10 rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-all resize-none" placeholder="Tell us about your event..." data-id="element-473"></textarea>
                </div>

                <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-charcoal hover:bg-amber text-white font-heading text-xl py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider disabled:opacity-70" data-id="element-474">
                  {formStatus === 'submitting' ? 'Sending Request...' : 'Submit Catering Request'}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </motion.div>;
}